// Neo's account preference changes — the pure half: decoding the SSE record
// and the chat chip's wording. Every Neo surface that streams a turn reads it.
//
//   preferences  { changes }  — account settings Neo CHANGED (ADR 0225). Today
//                               only units; the surface refreshes the account
//                               so every page re-renders in the new units.
//
// Decoded defensively, like the alarm records: a malformed entry is dropped,
// never rendered half-filled.

import { parseUnitSettings, type UnitSettings } from '@neomaris/protocol';

/** A preference Neo changed. Reported whatever the model's reply says. */
export interface NeoPreferenceChange {
  kind: 'units';
  summary: string;
  units: UnitSettings;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function listOf(payload: unknown): unknown[] {
  const list = isRecord(payload) ? payload.changes : payload;
  return Array.isArray(list) ? list : [];
}

/** Decode a `preferences` event (or the blocking route's `preferences`). */
export function parsePreferenceChanges(payload: unknown): NeoPreferenceChange[] {
  const out: NeoPreferenceChange[] = [];
  for (const raw of listOf(payload)) {
    if (!isRecord(raw) || raw.kind !== 'units') continue;
    const summary = stringOf(raw.summary);
    const units = parseUnitSettings(raw.units);
    if (summary === undefined || units === null) continue;
    out.push({ kind: 'units', summary, units });
  }
  return out;
}

/** The chat chip for a preference change. */
export function preferenceChip(change: NeoPreferenceChange): string {
  return `✓ Units updated — ${change.summary}`;
}
